/**
 * Workbench — controlled draft lane deep panel (context / proposal / receipt / recovery).
 * Projection-only · grantsAuthority=false.
 */
import React, { useMemo } from 'react';
import { Alert, Space, Tag, Typography } from 'antd';
import { feSafeWorkbenchControlledLaneViewV1 } from '@shared/ai-controlled-execution-representative-lane/surfaces/feSafeDemoProjectionV1';

const { Text, Paragraph } = Typography;

export interface ControlledDraftLanePanelProps {
  tenantId: string;
}

export function ControlledDraftLanePanel({
  tenantId: _tenantId,
}: ControlledDraftLanePanelProps): React.ReactElement {
  const vm = useMemo(() => feSafeWorkbenchControlledLaneViewV1(), []);

  return (
    <section
      data-testid="ai-controlled-draft-lane-panel"
      aria-label="AI controlled draft lane"
      style={{ marginTop: 12 }}
    >
      <Space wrap style={{ marginBottom: 8 }}>
        <Text strong>Controlled draft lane</Text>
        <Tag data-testid="ai-lane-ui-state">{vm.uiState}</Tag>
        <Tag data-testid="ai-lane-grants-authority">grantsAuthority=false</Tag>
        <Tag data-testid="ai-lane-provenance">{vm.provenance}</Tag>
      </Space>
      <Paragraph data-testid="ai-lane-proposal">{vm.proposalDetails}</Paragraph>
      <dl>
        <div>
          <dt>FlowPurpose</dt>
          <dd data-testid="ai-lane-flow">{vm.flowPurpose}</dd>
        </div>
        <div>
          <dt>Expected effect</dt>
          <dd data-testid="ai-lane-effect">{vm.expectedEffect}</dd>
        </div>
        <div>
          <dt>Authority</dt>
          <dd data-testid="ai-lane-authority">{vm.authorityDecision}</dd>
        </div>
        <div>
          <dt>Approval</dt>
          <dd data-testid="ai-lane-approval">{vm.approvalStatus}</dd>
        </div>
        <div>
          <dt>Execution</dt>
          <dd data-testid="ai-lane-execution">{vm.executionStatus}</dd>
        </div>
        <div>
          <dt>Receipt</dt>
          <dd data-testid="ai-lane-receipt">{vm.receiptId ?? 'none'}</dd>
        </div>
        <div>
          <dt>Assumptions</dt>
          <dd data-testid="ai-lane-assumptions">{vm.factsVsAssumptions.assumptions.join(' · ')}</dd>
        </div>
      </dl>
      {vm.recovery ? (
        <Alert type="warning" showIcon message={vm.recovery} data-testid="ai-lane-recovery" />
      ) : null}
    </section>
  );
}
